/**
 * TransferScanningStep — box scanning UI for transfer verification.
 * Extracted from TransferVerification.tsx for readability.
 */
import React from 'react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { UnprocessedTransfer, TransferItem } from '../../../services/inventoryService';
import ScanSummaryModal from '../components/ScanSummaryModal';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  X,
  Scan,
  AlertTriangle,
  Plus,
  Pause,
} from 'lucide-react';

export type DecisionMode = 'perItem' | 'wholeTransfer';

export interface ScannedBox {
  gtin: string;
  weight: number;
  quantity?: number;
  is_packing_material?: boolean;
  item_number?: string;
  item_name?: string;
  status: 'accepted' | 'rejected' | 'on_hold';
  notInTransfer?: boolean;
  rejectReason?: string;
  onHoldReason?: string;
  manualReason?: string;
}

export interface PendingBox {
  gtin: string;
  weight: number;
  quantity?: number;
  is_packing_material?: boolean;
  item_number: string;
  item_name?: string;
  notInTransfer: boolean;
}

interface TransferScanningStepProps {
  selectedTransfer: UnprocessedTransfer | null;
  setStep: (step: 'badge' | 'transfer' | 'scanning' | 'decision') => void;
  setSelectedTransfer: (t: UnprocessedTransfer | null) => void;
  scannedBoxes: ScannedBox[];
  setScannedBoxes: React.Dispatch<React.SetStateAction<ScannedBox[]>>;
  decisionMode: DecisionMode;
  setDecisionMode: (m: DecisionMode) => void;
  pendingBox: PendingBox | null;
  scanError: string;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  processBoxScan: (barcode: string) => Promise<void>;
  handleBoxDecision: (status: 'accepted' | 'rejected' | 'on_hold', reason?: string) => void;
  removeScannedBox: (index: number) => void;
  showRejectReasons: boolean;
  setShowRejectReasons: (v: boolean) => void;
  showHoldReasons: boolean;
  setShowHoldReasons: (v: boolean) => void;
  showManualAdd: boolean;
  setShowManualAdd: (v: boolean) => void;
  manualSelectedItem: string | null;
  setManualSelectedItem: (v: string | null) => void;
  manualWeight: string;
  setManualWeight: (v: string) => void;
  manualWeightRef: React.RefObject<HTMLInputElement>;
  handleManualAddConfirm: (reason: string) => void;
  showScanSummary: boolean;
  setShowScanSummary: (v: boolean) => void;
  handleProceedToDecision: () => void;
  inputRef: React.RefObject<HTMLInputElement>;
  /** When true the transfer was pre-selected (e.g. from the detail page) — hide the back arrow */
  isPreSelected?: boolean;
  /** Navigate back to transfers list (mobile) */
  onBackToList?: () => void;
}

const TransferScanningStep: React.FC<TransferScanningStepProps> = ({
  selectedTransfer,
  setStep,
  setSelectedTransfer,
  scannedBoxes,
  setScannedBoxes,
  decisionMode,
  setDecisionMode,
  pendingBox,
  scanError,
  handleKeyDown,
  processBoxScan,
  handleBoxDecision,
  removeScannedBox,
  showRejectReasons,
  setShowRejectReasons,
  showHoldReasons,
  setShowHoldReasons,
  showManualAdd,
  setShowManualAdd,
  manualSelectedItem,
  setManualSelectedItem,
  manualWeight,
  setManualWeight,
  manualWeightRef,
  handleManualAddConfirm,
  showScanSummary,
  setShowScanSummary,
  handleProceedToDecision,
  inputRef,
  isPreSelected = false,
  onBackToList,
}) => {
  const { t } = useLanguage();

  if (!selectedTransfer) return null;
  const pendingItems = selectedTransfer.items.filter((i: TransferItem) => !i.already_received);

  return (
    <div className="p-4 overflow-x-hidden">
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => {
            if (isPreSelected && onBackToList) {
              onBackToList();
            } else {
              setStep('transfer'); setSelectedTransfer(null); setScannedBoxes([]);
            }
          }}
          className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {selectedTransfer.transfer_number}
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('transferVerification.scanBoxes')}
          </p>
        </div>
      </div>


      {/* Running totals per item */}
      <div className="mb-4 bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 px-3 py-2 border-b border-gray-200 dark:border-gray-600">
          {t('transferVerification.expectedItems')}
        </h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-600">
              <th className="text-left px-2 py-1.5 font-medium">{t('transferVerification.item')}</th>
              <th className="text-center px-1 py-1.5 font-medium">{t('transferVerification.expected')}</th>
              <th className="text-center px-1 py-1.5 font-medium">{t('transferVerification.scanned')}</th>
              <th className="text-right px-2 py-1.5 font-medium">lbs</th>
            </tr>
          </thead>
          <tbody>
            {pendingItems.map((item: TransferItem) => {
              const accepted = scannedBoxes.filter(b => b.item_number === item.item_number && b.status === 'accepted');
              const onHold = scannedBoxes.filter(b => b.item_number === item.item_number && b.status === 'on_hold');
              const rejected = scannedBoxes.filter(b => b.item_number === item.item_number && b.status === 'rejected');
              const totalScanned = accepted.length + onHold.length + rejected.length;
              const acceptedWeight = accepted.reduce((s, b) => s + (b.weight || 0), 0);
              const totalWeight = scannedBoxes.filter(b => b.item_number === item.item_number).reduce((s, b) => s + (b.weight || 0), 0);
              const acceptedQty = accepted.reduce((s, b) => s + (b.quantity || 1), 0);
              const isPacking = item.is_packing_material === 'Y';
              return (
                <React.Fragment key={item.item_number}>
                  <tr className="border-b border-gray-200 dark:border-gray-600 last:border-0">
                    <td className="px-2 py-1.5">
                      <span className="text-gray-800 dark:text-gray-200 font-medium text-xs">
                        {item.item_name || item.item_number}
                      </span>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400">{item.item_number}</p>
                    </td>
                    <td className="text-center px-1 py-1.5 text-gray-700 dark:text-gray-300 font-medium text-sm">
                      {item.expected_quantity}
                    </td>
                    <td className="text-center px-1 py-1.5">
                      <span className="text-green-600 dark:text-green-400 font-medium text-sm">
                        {isPacking ? acceptedQty : totalScanned}
                      </span>
                    </td>
                    <td className="text-right px-2 py-1.5 text-sm text-gray-700 dark:text-gray-300 font-mono">
                      {!isPacking && totalWeight > 0 ? totalWeight.toFixed(2) : '-'}
                    </td>
                  </tr>
                  {!isPacking && totalWeight > 0 && (
                    <tr className="border-b border-gray-200 dark:border-gray-600 last:border-0">
                      <td colSpan={4} className="px-2 py-1 text-right text-xs text-gray-500 dark:text-gray-400">
                        {t('transferVerification.accepted')}: {accepted.length}
                        {onHold.length > 0 && <> · {t('transferVerification.hold')}: {onHold.length}</>}
                        {rejected.length > 0 && <> · {t('transferVerification.rejected')}: {rejected.length}</>}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pending box decision prompt */}
      {pendingBox && (
        <div className={`mb-4 p-4 border-2 rounded-lg ${pendingBox.notInTransfer
            ? 'bg-red-50 dark:bg-red-900/30 border-red-400 dark:border-red-600'
            : 'bg-amber-50 dark:bg-amber-900/30 border-amber-400 dark:border-amber-600'
          }`}>
          {/* Warning if not in transfer */}
          {pendingBox.notInTransfer && (
            <div className="flex items-center gap-2 mb-3 p-2 bg-red-100 dark:bg-red-900/50 rounded text-red-700 dark:text-red-300 text-sm">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">{t('transferVerification.notInTransferWarning')}</span>
            </div>
          )}
          <div className="text-center mb-3">
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {pendingBox.item_name || pendingBox.item_number}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">{pendingBox.item_number}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
              {pendingBox.is_packing_material
                ? (() => {
                    const subQty = selectedTransfer?.items.find(i => i.item_number === pendingBox.item_number)?.item_sub_quantity;
                    const totalPcs = subQty ? (pendingBox.quantity || 0) * subQty : 0;
                    return totalPcs > 0 ? `${pendingBox.quantity} ${t('transferVerification.qty')} (${totalPcs} pcs)` : `${pendingBox.quantity} ${t('transferVerification.qty')}`;
                  })()
                : `${Number.isFinite(pendingBox.weight) ? pendingBox.weight.toFixed(2) : '—'} lbs`}
            </p>
          </div>

          {/* Hold reason selection */}
          {showHoldReasons && (
            <div className="mb-3 p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
              <p className="text-sm text-amber-800 dark:text-amber-200 font-medium mb-2">
                {t('transferVerification.selectHoldReason')}
              </p>
              <div className="flex flex-col gap-2">
                <button onClick={() => handleBoxDecision('on_hold', 'temperature')} className="w-full py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 font-medium">
                  {t('transferVerification.onHoldReasons.temperature')}
                </button>
                <button onClick={() => handleBoxDecision('on_hold', 'wrongItemTomorrow')} className="w-full py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 font-medium">
                  {t('transferVerification.onHoldReasons.wrongItemTomorrow')}
                </button>
                <button onClick={() => handleBoxDecision('on_hold', 'other')} className="w-full py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 font-medium">
                  {t('transferVerification.onHoldReasons.other')}
                </button>
                <button onClick={() => setShowHoldReasons(false)} className="w-full py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}

          {/* Reject reason selection */}
          {showRejectReasons && (
            <div className="mb-3 p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <p className="text-sm text-red-800 dark:text-red-200 font-medium mb-2">
                {t('transferVerification.selectRejectReason')}
              </p>
              <div className="flex flex-col gap-2">
                <button onClick={() => handleBoxDecision('rejected', 'Quality Issue')} className="w-full py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium">
                  {t('transferVerification.qualityIssue')}
                </button>
                <button onClick={() => handleBoxDecision('rejected', 'Temperature Issue')} className="w-full py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium">
                  {t('transferVerification.temperatureIssue')}
                </button>
                <button onClick={() => handleBoxDecision('rejected', 'Box not intact')} className="w-full py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium">
                  {t('transferVerification.boxNotIntact')}
                </button>
                <button onClick={() => handleBoxDecision('rejected', 'Wrong item')} className="w-full py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium">
                  {t('transferVerification.wrongItem')}
                </button>
                <button onClick={() => setShowRejectReasons(false)} className="w-full py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          )}

          {/* Action buttons */}
          {!showRejectReasons && !showHoldReasons && (
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => handleBoxDecision('accepted')} className="py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold flex items-center justify-center gap-2">
                <Check className="w-5 h-5" />
                {t('transferVerification.accept')}
              </button>
              <button onClick={() => setShowHoldReasons(true)} className="py-3 bg-amber-500 text-white rounded-lg hover:bg-amber-600 font-semibold flex items-center justify-center gap-2">
                <Pause className="w-5 h-5" />
                {t('transferVerification.hold')}
              </button>
              <button onClick={() => setShowRejectReasons(true)} className="py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold flex items-center justify-center gap-2">
                <X className="w-5 h-5" />
                {t('transferVerification.reject')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Scan input & manual add - only show when no pending box */}
      {!pendingBox && (
        <>
          <input
            ref={inputRef}
            type="text"
            onKeyDown={handleKeyDown}
            onPaste={async (e) => {
              e.preventDefault();
              const pasted = e.clipboardData.getData('text').trim();
              if (pasted) {
                await processBoxScan(pasted);
                if (inputRef.current) inputRef.current.value = '';
              }
            }}
            placeholder={t('transferVerification.waitingForScan')}
            className="w-full px-4 py-3 text-lg border-2 border-blue-300 dark:border-blue-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center mb-3"
            autoComplete="off"
            inputMode="none"
            onTouchStart={(e) => { e.currentTarget.inputMode = 'text'; }}
          />

          {/* Manual Add — for damaged labels that can't be scanned */}
          {!showManualAdd ? (
            <button
              onClick={() => setShowManualAdd(true)}
              className="w-full mb-3 py-2 flex items-center justify-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              <Plus className="w-4 h-4" />
              {t('transferVerification.manualAdd')}
            </button>
          ) : (
            <div className="mb-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  {t('transferVerification.manualAddTitle')}
                </span>
                <button
                  onClick={() => { setShowManualAdd(false); setManualSelectedItem(null); setManualWeight(''); }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Item buttons */}
              {!manualSelectedItem ? (
                <div className="space-y-2">
                  <p className="text-xs text-amber-700 dark:text-amber-300">{t('transferVerification.selectItem')}</p>
                  <div className={`grid gap-2 ${pendingItems.length >= 3 ? 'grid-cols-3' : pendingItems.length === 2 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    {pendingItems.map((item: TransferItem) => (
                      <button
                        key={item.item_number}
                        onClick={() => { setManualSelectedItem(item.item_number); setTimeout(() => manualWeightRef.current?.focus(), 100); }}
                        className="w-full px-3 py-2 text-left bg-white dark:bg-gray-700 border border-amber-300 dark:border-amber-600 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-800/30 text-gray-800 dark:text-gray-200 transition-colors"
                      >
                        <span className="text-sm font-medium block">{item.item_name || item.item_number}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{item.item_number}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Selected item + weight in one row */}
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-700 dark:text-gray-300 font-medium truncate">
                          {pendingItems.find(i => i.item_number === manualSelectedItem)?.item_name || manualSelectedItem}
                        </span>
                        <button
                          onClick={() => { setManualSelectedItem(null); setManualWeight(''); }}
                          className="text-xs text-amber-600 dark:text-amber-400 hover:underline flex-shrink-0"
                        >
                          {t('common.change')}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{manualSelectedItem}</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <input
                        ref={manualWeightRef}
                        type="number"
                        inputMode={pendingItems.find(i => i.item_number === manualSelectedItem)?.is_packing_material === 'Y' ? 'numeric' : 'decimal'}
                        step={pendingItems.find(i => i.item_number === manualSelectedItem)?.is_packing_material === 'Y' ? '1' : '0.01'}
                        min="0"
                        value={manualWeight}
                        onChange={(e) => setManualWeight(e.target.value)}
                        placeholder={pendingItems.find(i => i.item_number === manualSelectedItem)?.is_packing_material === 'Y' ? '0' : '0.00'}
                        className="w-24 px-2 py-1.5 text-sm text-right border border-amber-300 dark:border-amber-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {pendingItems.find(i => i.item_number === manualSelectedItem)?.is_packing_material === 'Y' ? t('transferVerification.qty') : 'lbs'}
                      </span>
                    </div>
                  </div>

                  {/* Reason buttons — horizontal row, shown once weight is entered */}
                  {manualWeight && parseFloat(manualWeight) > 0 && (
                    <div className="space-y-1.5">
                      <p className="text-xs text-amber-700 dark:text-amber-300">{t('transferVerification.manualAddReason')}</p>
                      <div className="grid grid-cols-3 gap-1.5">
                        <button onClick={() => handleManualAddConfirm('Damaged Label')} className="py-2 text-xs font-medium bg-amber-500 text-white rounded-lg hover:bg-amber-600">
                          {t('transferVerification.damagedLabel')}
                        </button>
                        <button onClick={() => handleManualAddConfirm('Missing Label')} className="py-2 text-xs font-medium bg-amber-500 text-white rounded-lg hover:bg-amber-600">
                          {t('transferVerification.missingLabel')}
                        </button>
                        <button onClick={() => handleManualAddConfirm('Label Unreadable')} className="py-2 text-xs font-medium bg-amber-500 text-white rounded-lg hover:bg-amber-600">
                          {t('transferVerification.labelUnreadable')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {scanError && (
        <div className="mb-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-300">
          {scanError}
        </div>
      )}

      {/* Scanned boxes list */}
      {scannedBoxes.length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('transferVerification.scannedItems')} ({scannedBoxes.length})
          </h3>
          <div className="max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-100 dark:bg-gray-800">
                <tr className="text-gray-500 dark:text-gray-400">
                  <th className="text-left py-1 px-2 font-medium">#</th>
                  <th className="text-left py-1 px-2 font-medium">{t('transferVerification.item')}</th>
                  <th className="text-right py-1 px-2 font-medium">lbs</th>
                  <th className="text-center py-1 px-2 font-medium w-6"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {[...scannedBoxes].reverse().map((box, reverseIdx) => {
                  const idx = scannedBoxes.length - 1 - reverseIdx;
                  const statusColor = box.status === 'accepted' ? 'text-green-600 dark:text-green-400'
                    : box.status === 'on_hold' ? 'text-amber-600 dark:text-amber-400'
                    : 'text-red-600 dark:text-red-400';
                  return (
                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="py-1 px-2 text-gray-400 font-mono">{idx + 1}</td>
                      <td className="py-1 px-2">
                        <div className="flex items-center gap-1">
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${box.status === 'accepted' ? 'bg-green-500' : box.status === 'on_hold' ? 'bg-amber-500' : 'bg-red-500'}`} />
                          <span className="text-gray-700 dark:text-gray-300 truncate">{box.item_number}{box.item_name ? ` — ${box.item_name}` : ''}</span>
                          {box.manualReason && <span className="text-[10px] px-1 bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 rounded">M</span>}
                          {box.notInTransfer && <span className="text-[10px] px-1 bg-orange-200 dark:bg-orange-800 text-orange-800 dark:text-orange-200 rounded">!</span>}
                        </div>
                      </td>
                      <td className="py-1 px-2 text-right text-gray-900 dark:text-white font-mono whitespace-nowrap">
                        {box.is_packing_material ? `${box.quantity || 0}` : `${Number.isFinite(box.weight) ? box.weight.toFixed(2) : '—'}`}
                      </td>
                      <td className="py-1 px-1 text-center">
                        <button onClick={() => removeScannedBox(idx)} className="text-gray-400 hover:text-red-500 p-0.5">
                          <X className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Proceed button - only when no pending box */}
      {!pendingBox && scannedBoxes.length > 0 && (
        <button
          onClick={() => setShowScanSummary(true)}
          className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 text-lg font-medium"
        >
          {t('transferVerification.proceedToDecision')}
          <ArrowRight className="w-5 h-5" />
        </button>
      )}

      {/* Scan summary modal — scanned vs expected comparison */}
      {selectedTransfer && (
        <ScanSummaryModal
          isOpen={showScanSummary}
          scannedBoxes={scannedBoxes}
          transferItems={selectedTransfer.items}
          onGoBack={() => setShowScanSummary(false)}
          onConfirm={() => { setShowScanSummary(false); handleProceedToDecision(); }}
        />
      )}
    </div>
  );
};

export default TransferScanningStep;
